import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Link, Navigate, Routes, useNavigate, useLocation, useParams } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { scrubCreationSelections } from './utils/productCreationRecovery';
import Home from './components/Home';
import ProductList from './components/ProductList';
import LivestreamPrep from './components/LivestreamPrep';
import AddProduct from './components/AddProduct';


import BatchAddProduct from './components/BatchAddProduct';
import EditProduct from './components/EditProduct';
import ViewProduct from './components/ViewProduct';
import EditRates from './components/EditRates';
import VendorManagement from './components/VendorManagement';
import QuickCalculator from './components/QuickCalculator';
import Login from './components/Login';
import AuthSettings from './components/AuthSettings';
import { auth } from './firebaseConfig';
import { getAuthorizedUserRole } from './utils/authConfig';
// 天藍小舖換貨系統
import TianlanCustomerQuery from './components/tianlan/TianlanJamboCustomerQuery';
import TianlanSecureLinkLoading from './components/tianlan/TianlanSecureLinkLoading';
import TianlanExchangeHome from './components/tianlan/TianlanExchangeHome';
import TianlanProductManager from './components/tianlan/TianlanJamboRuleManager';
import TianlanOrderExchangeManager from './components/tianlan/TianlanJamboOrderManager';
import TianlanSettings from './components/tianlan/TianlanSettings';
import TianlanJamboStoreManager from './components/tianlan/TianlanJamboStoreManager';
import TianlanStoreSwitcher from './components/tianlan/TianlanStoreSwitcher';
import TianlanAdminShell from './components/tianlan/TianlanAdminShell';
import usePageTitle from './hooks/usePageTitle';
import { VALID_COUNTRY_IDS, isSimpleProductCountry } from './utils/countryConfig';
import { ToastContainer } from './components/Toast';
import './styles.css';
import './tianlan-redesign.css';

// Compatibility only: old quick-intake bookmarks return to the original form.
function OriginalAddRedirect() {
  const { country } = useParams();
  const location = useLocation();
  return <Navigate replace to={`/add/${country}${location.search}`} />;
}

// 使用 UUID 取代原有的 deviceId，並僅保存 24 小時
function getOrCreateDeviceId() {
  const storedData = localStorage.getItem('deviceIdData');
  const now = Date.now();

  if (storedData) {
    try {
      const { deviceId, timestamp } = JSON.parse(storedData);
      if (now - timestamp < 24 * 60 * 60 * 1000) {
        return deviceId;
      }
    } catch (error) {
      console.error("解析 deviceIdData 失敗：", error);
    }
  }

  const newDeviceId = crypto.randomUUID();
  const newData = {
    deviceId: newDeviceId,
    timestamp: now
  };
  localStorage.setItem('deviceIdData', JSON.stringify(newData));
  return newDeviceId;
}

// 動態標題組件
function DynamicTitle() {
  const location = useLocation();
  
  // 根據路由設置不同的標題和描述
  const getPageInfo = () => {
    const path = location.pathname;
    
    // 檢查是否在登入頁面
    const isLoginPage = !auth.currentUser && !['/tianlan/customer', '/tianlan/service'].includes(path);
    
    if (path === '/') {
      return {
        title: '首頁',
        description: '天藍小舖管理系統首頁，選擇國家開始管理商品'
      };
    }
    if (path.includes('/products/')) {
      const country = path.split('/')[2];
      return {
        title: '商品列表',
        description: `查看和管理 ${country} 的商品庫存`
      };
    }
    if (path.includes('/add/')) {
      const country = path.split('/')[2];
      return {
        title: '新增商品',
        description: `為 ${country} 添加新的商品`
      };
    }
    if (path.includes('/batch-add/')) {
      const country = path.split('/')[2];
      return {
        title: '批次新增商品',
        description: `批量為 ${country} 添加多個商品`
      };
    }
    if (path.includes('/edit-rates/')) {
      const country = path.split('/')[2];
      return {
        title: '編輯匯率運費',
        description: `設定 ${country} 的匯率和運費`
      };
    }
    if (path.includes('/view/')) {
      return {
        title: '商品詳情',
        description: '查看商品的詳細信息和庫存狀況'
      };
    }
    if (path.includes('/edit/')) {
      return {
        title: '編輯商品',
        description: '修改商品信息和設定'
      };
    }
    if (path.includes('/vendor/')) {
      const country = path.split('/')[2];
      return {
        title: '廠商管理',
        description: `管理 ${country} 的廠商信息`
      };
    }
    if (path.includes('/quick-calculator/')) {
      const country = path.split('/')[2];
      return {
        title: '快速計算',
        description: `快速計算 ${country} 商品的價格和成本`
      };
    }
    if (path === '/exchange/tianlan') {
      return {
        title: '天藍小舖換貨管理',
        description: '天藍小舖直播換貨系統管理首頁'
      };
    }
    if (path === '/exchange/tianlan/products') {
      return {
        title: '商品規則管理',
        description: '管理商品換貨與售後表單任務規則'
      };
    }
    if (path === '/exchange/tianlan/orders') {
      return {
        title: '訂單售後服務後台',
        description: '處理直播訂單換貨與售後表單任務'
      };
    }
    if (path === '/exchange/tianlan/forms') {
      return {
        title: '訂單售後服務後台',
        description: '建立換貨與售後表單任務'
      };
    }
    if (path === '/exchange/tianlan/settings') {
      return {
        title: '系統設定',
        description: '配置天藍小舖系統參數和設定'
      };
    }
    if (['/tianlan/customer', '/tianlan/service'].includes(path)) {
      return {
        title: '訂單售後服務',
        description: '客戶查詢換貨與售後表單任務'
      };
    }
    if (isLoginPage) {
      return {
        title: '系統登入',
        description: '登入天藍小舖管理系統'
      };
    }
    
    return {
      title: '天藍小舖管理系統',
      description: '天藍小舖商品管理和換貨系統'
    };
  };
  
  const pageInfo = getPageInfo();
  usePageTitle(pageInfo.title, '天藍小舖', pageInfo.description);
  
  return null;
}

const menuLinkStyle = {
  display: 'block',
  padding: '0.75rem 1rem',
  color: '#333',
  textDecoration: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s',
  fontSize: '0.95rem'
};

function AppContent() {
  const [country, setCountry] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 檢查當前路徑是否為客戶查詢頁面（不需要登入）
  const isCustomerPage = ['/tianlan/customer', '/tianlan/service'].includes(window.location.pathname);

  // 監聽路由變化，自動設置 country
  useEffect(() => {
    const path = location.pathname;
    
    // 如果是首頁，清除 country
    if (path === '/') {
      setCountry(null);
      return;
    }
    
    // 如果是天藍小舖換貨系統
    if (path.startsWith('/exchange/tianlan')) {
      setCountry('tianlan-exchange');
      return;
    }
    
    // 從路徑中提取 country
    // 例如：/products/korea -> korea
    // /add/korea -> korea
    // /view/korea/xxx -> korea
    const pathParts = path.split('/');
    if (pathParts.length >= 3) {
      const extractedCountry = pathParts[2];
      // 驗證是否為有效的國家代碼
      if (VALID_COUNTRY_IDS.includes(extractedCountry)) {
        setCountry(extractedCountry);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    getOrCreateDeviceId();

    localStorage.removeItem('adminSession');
    localStorage.removeItem('lastLogin');
    localStorage.removeItem('deviceId');

    let recoveryOwner = auth.currentUser?.uid;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (recoveryOwner && recoveryOwner !== user?.uid) scrubCreationSelections(recoveryOwner);
      recoveryOwner = user?.uid;
      if (!user) {
        setIsLoggedIn(false);
        setUserRole(null);
        setIsLoading(false);
        return;
      }

      try {
        const role = await getAuthorizedUserRole(user);
        if (!role) {
          await signOut(auth);
          setIsLoggedIn(false);
          setUserRole(null);
          return;
        }
        setUserRole(role);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Firebase Auth 會話驗證失敗:', error);
        await signOut(auth);
        setIsLoggedIn(false);
        setUserRole(null);
      } finally {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const handleCountrySelect = (selectedCountry) => {
    if (selectedCountry === 'tianlan-exchange') {
      setCountry('tianlan-exchange');
      navigate('/exchange/tianlan');
    } else {
      setCountry(selectedCountry);
      // 直接進入商品管理，不需要通行碼驗證
      navigate(`/products/${selectedCountry}`);
    }
  };

  const handleLogin = (role) => {
    setUserRole(role);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem('adminSession');
    sessionStorage.removeItem('countryAccess');
    localStorage.removeItem('lastLogin');
    localStorage.removeItem('deviceId');
    
    setIsLoggedIn(false);
    setUserRole(null);
    setCountry('');
    navigate('/');
  };

  if (isLoading) {
    return isCustomerPage && new URLSearchParams(location.search).get('t')
      ? <TianlanSecureLinkLoading /> : <div>Loading...</div>;
  }

  // 客戶查詢頁面不需要登入
  if (isCustomerPage) {
    return (
      <div className="App">
        <DynamicTitle />
        <Routes>
          <Route path="/tianlan/customer" element={<TianlanCustomerQuery />} />
          <Route path="/tianlan/service" element={<TianlanCustomerQuery />} />
        </Routes>
      </div>
    );
  }

  // 客戶換貨頁面不需要登入，直接顯示
  if (!isLoggedIn && !isCustomerPage) {
    return (
      <div className="App">
        <DynamicTitle />
        <Login onLogin={handleLogin} />
      </div>
    );
  }


  // 檢查路徑是否為首頁
  const isHomePage = location.pathname === '/';
  
  return (
    <div className="App">
      <DynamicTitle />
      <ToastContainer />
      {!country && isHomePage ? (
        <Home
          onCountrySelect={handleCountrySelect}
          isAdmin={userRole === 'admin'}
          onOpenAuthSettings={() => navigate('/auth-settings')}
        />
      ) : country === 'tianlan-exchange' ? (
        <TianlanAdminShell onLogout={handleLogout}>
          <Routes>
            <Route path="/exchange/tianlan" element={<TianlanExchangeHome />} />
            <Route path="/exchange/tianlan/products" element={<TianlanProductManager />} />
            <Route path="/exchange/tianlan/orders" element={<TianlanOrderExchangeManager />} />
            <Route path="/exchange/tianlan/forms" element={<Navigate to="/exchange/tianlan/orders" replace />} />
            <Route path="/exchange/tianlan/stores" element={<TianlanJamboStoreManager />} />
            <Route path="/exchange/tianlan/settings" element={<TianlanSettings />} />
          </Routes>
        </TianlanAdminShell>
      ) : (
        <>
          <nav style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: '#fff',
            borderBottom: '1px solid #e5e5e5',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <button 
              onClick={() => navigate(-1)} 
              style={{
                background: 'transparent',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                color: '#666',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
              onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
            >
              ← 返回
            </button>
            <button 
              onClick={() => setIsCollapsed(!isCollapsed)} 
              style={{
                background: '#333',
                border: 'none',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#555'; }}
              onMouseOut={(e) => { e.target.style.background = '#333'; }}
            >
              {isCollapsed ? "☰ 選單" : "✕ 關閉"}
            </button>
            <button 
              onClick={handleLogout} 
              style={{
                background: 'transparent',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                color: '#666',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
              onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
            >
              登出
            </button>
            {country === 'tianlan-exchange' && (
              <TianlanStoreSwitcher compact />
            )}
            {!isCollapsed && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                borderBottom: '1px solid #e5e5e5',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                padding: '1rem'
              }}>
                {country === 'tianlan-exchange' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Link 
                      to="/exchange/tianlan/products" 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >商品規則管理</Link>
                    <Link 
                      to="/exchange/tianlan/orders" 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >訂單售後服務後台</Link>
                    <Link
                      to="/exchange/tianlan/stores"
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >直播商店管理</Link>
                    <Link 
                      to="/exchange/tianlan/settings" 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >系統設定／前台樣式</Link>
                    <a 
                      href="/tianlan/customer" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >客戶售後查詢頁面 (新視窗)</a>
                    <a
                      href="/tianlan/service"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >售後服務頁面 (新視窗)</a>
                  </div>
                ) : country ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Link 
                      to={`/products/${country}`} 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >商品列表</Link>
                    {VALID_COUNTRY_IDS.includes(country) && <Link
                      to={`/prep/${country}`}
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >備播清單</Link>}
                    <Link 
                      to={`/add/${country}`}
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >新增商品</Link>
                    <Link 
                      to={`/batch-add/${country}`} 
                      style={menuLinkStyle}
                      onClick={() => setIsCollapsed(true)}
                      onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                      onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                    >批次新增商品</Link>
                    {!isSimpleProductCountry(country) && (
                      <Link 
                        to={`/edit-rates/${country}`} 
                        style={menuLinkStyle}
                        onClick={() => setIsCollapsed(true)}
                        onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                        onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                      >編輯匯率運費</Link>
                    )}
                    {!isSimpleProductCountry(country) && (
                      <Link 
                        to={`/vendor/${country}`} 
                        style={menuLinkStyle}
                        onClick={() => setIsCollapsed(true)}
                        onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                        onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                      >廠商管理</Link>
                    )}
                    {!isSimpleProductCountry(country) && (
                      <Link 
                        to={`/quick-calculator/${country}`} 
                        style={menuLinkStyle}
                        onClick={() => setIsCollapsed(true)}
                        onMouseOver={(e) => { e.target.style.background = '#f5f5f5'; }}
                        onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
                      >快速計算</Link>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </nav>
          <Routes>
            <Route path="/products/:country" element={<ProductList />} />
            <Route path="/search/:country" element={<ProductList />} />
            <Route path="/add/:country" element={<AddProduct />} />
            <Route path="/intake/:country" element={<OriginalAddRedirect />} />
            <Route path="/prep/:country" element={<LivestreamPrep userRole={userRole} />} />
            <Route path="/batch-add/:country" element={<BatchAddProduct />} />
            <Route path="/edit-rates/:country" element={<EditRates />} />
            <Route path="/view/:country/:id" element={<ViewProduct />} />
            <Route path="/edit/:country/:id" element={<EditProduct />} />
            <Route path="/vendor/:country" element={<VendorManagement />} />
            <Route path="/quick-calculator/:country" element={<QuickCalculator />} />
            <Route
              path="/auth-settings"
              element={userRole === 'admin' ? <AuthSettings /> : <Navigate to="/" replace />}
            />
            {/* 天藍小舖換貨系統路由 - 需要登入 */}
            <Route path="/exchange/tianlan" element={<TianlanExchangeHome />} />
            <Route path="/exchange/tianlan/products" element={<TianlanProductManager />} />
            <Route path="/exchange/tianlan/orders" element={<TianlanOrderExchangeManager />} />
            <Route path="/exchange/tianlan/forms" element={<Navigate to="/exchange/tianlan/orders" replace />} />
            <Route path="/exchange/tianlan/stores" element={<TianlanJamboStoreManager />} />
            <Route path="/exchange/tianlan/settings" element={<TianlanSettings />} />
          </Routes>
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* 客戶換貨頁面 - 公開訪問，不需要密碼 */}
        <Route path="/tianlan/customer" element={<TianlanCustomerQuery />} />
        <Route path="/tianlan/service" element={<TianlanCustomerQuery />} />
        
        {/* 所有其他頁面 - 需要登入認證 */}
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;
